import {useRef, useState} from 'react';
import {useTranslation} from "react-i18next";
import {API_BASE} from '../../../apiBase';
import {apiFetch} from '../../../api/api';


export function useDrawAnnotation({
                                      pageRefs,
                                      scale,
                                      onAddAnnotation,
                                      onFinishDrawing,
                                      isDrawing,
                                      updateAnnotation,
                                      provider,
                                      model,
                                      suppressProcessing = false
                                  }) {
    const {t} = useTranslation();

    const containerRef = useRef();
    const [drawing, setDrawing] = useState(false);
    const [startPoint, setStartPoint] = useState(null);
    const [currentRect, setCurrentRect] = useState(null);
    const [currentPage, setCurrentPage] = useState(null);
    const [hasMoved, setHasMoved] = useState(false);

    const MARGIN = 2;
    // Offsets in PDF coordinate space (scale-independent)
    const Y_OFFSET_PDF = 10;
    const X_OFFSET_PDF = 10;

    const handleMouseDown = (e) => {
        if (!isDrawing) return;

        const clickedPage = pageRefs.current.find(page => {
            // anchor to the page container (will re-check canvas below)
            const rect = page.getBoundingClientRect();
            return e.clientX >= rect.left && e.clientX <= rect.right &&
                e.clientY >= rect.top && e.clientY <= rect.bottom;
        });

        if (!clickedPage) return;

        // use the inner canvas rect to avoid wrapper paddings/spacings
        const canvas = clickedPage.querySelector('canvas.react-pdf__Page__canvas');
        const pageRect = (canvas || clickedPage).getBoundingClientRect();
        // Convert PDF-space offset to screen-space offset
        const X_OFFSET = X_OFFSET_PDF * scale;
        const Y_OFFSET = Y_OFFSET_PDF * scale;
        setStartPoint({
            x: e.clientX - pageRect.left + X_OFFSET,
            y: e.clientY - pageRect.top + Y_OFFSET
        });
        setCurrentPage(parseInt(clickedPage.dataset.index, 10));
        setHasMoved(false);  // Reset
        setDrawing(true);
    };

    const handleMouseMove = (e) => {
        if (!drawing || !isDrawing || currentPage == null) return;

        const page = pageRefs.current[currentPage];
        const canvas = page?.querySelector('canvas.react-pdf__Page__canvas');
        const rect = (canvas || page).getBoundingClientRect();

        // Convert PDF-space offset to screen-space offset
        const X_OFFSET = X_OFFSET_PDF * scale;
        const Y_OFFSET = Y_OFFSET_PDF * scale;
        const x = e.clientX - rect.left + X_OFFSET;
        const y = e.clientY - rect.top + Y_OFFSET;

        if (!hasMoved) {
            setHasMoved(true);
            return; // Skip the first call to prevent jump
        }

        setCurrentRect({
            x: Math.min(startPoint.x, x),
            y: Math.min(startPoint.y, y),
            width: Math.abs(startPoint.x - x),
            height: Math.abs(startPoint.y - y),
        });
    };

    const cleanup = () => {
        setDrawing(false);
        setStartPoint(null);
        setCurrentRect(null);
        setCurrentPage(null);
    };

    const handleMouseUp = async () => {
        if (drawing && currentRect && currentPage != null) {
            const scaleFactor = 1 / scale;

            // Convert PDF-space offset to screen-space offset
            const X_OFFSET = X_OFFSET_PDF * scale;
            const Y_OFFSET = Y_OFFSET_PDF * scale;
            
            // 1) Derive a rect WITHOUT the visual offsets for backend/crop math
            const dataRect = {
                x: currentRect.x - X_OFFSET,
                y: currentRect.y - Y_OFFSET,
                width: currentRect.width,
                height: currentRect.height,
            };


            // 2) UI bbox (with offsets) - convert screen coords to PDF coords, then apply margin in PDF space
            const uiBBox = [
                currentRect.x * scaleFactor + MARGIN,
                currentRect.y * scaleFactor + MARGIN,
                (currentRect.x + currentRect.width) * scaleFactor - MARGIN,
                (currentRect.y + currentRect.height) * scaleFactor - MARGIN,
            ];

            // 3) API bbox (without offsets) - convert to PDF coords, then apply margin
            const apiBBox = [
                dataRect.x * scaleFactor + MARGIN,
                dataRect.y * scaleFactor + MARGIN,
                (dataRect.x + dataRect.width) * scaleFactor - MARGIN,
                (dataRect.y + dataRect.height) * scaleFactor - MARGIN,
            ];

            const pageNum = currentPage + 1;
            const stableKey = `${pageNum}_entity_${uiBBox.join('_')}`;

            // 🔇 Redraw-only mode: no backend call — just add a clean entity box
            if (suppressProcessing) {
                onAddAnnotation({
                    page: pageNum,
                    type: 'entity',
                    bbox: uiBBox,
                    __raw_bbox: apiBBox,
                    text: '',
                    __uid: stableKey,
                });
                if (typeof onFinishDrawing === 'function') onFinishDrawing();
                cleanup();
                return;
            }

            const tempAnnotation = {
                page: pageNum,
                type: 'custom',
                bbox: uiBBox,
                __raw_bbox: apiBBox,
                text: 'Processing...',
                status: 'loading',
                __uid: stableKey, // keep the same scheme, helps jiggling later
            };

            onAddAnnotation(tempAnnotation); // Show temp annotation

            // Extract image from canvas
            const pageElement = pageRefs.current[currentPage];
            const canvas = pageElement?.querySelector('canvas.react-pdf__Page__canvas');
            if (!canvas) {
                console.error('❌ Canvas not found in page element. Drawing aborted.');
                updateAnnotation(stableKey, {
                    __replaceWith: {
                        type: 'entity',
                        page: pageNum,
                        bbox: uiBBox,
                        text: 'Canvas not found',
                        entity_type: 'error',
                    },
                    status: 'error',
                });
                cleanup();
                return;
            }

            const dpr = window.devicePixelRatio || 1;
            // PDF coords → canvas bitmap px == (bbox * scale) * DPR
            const [x0pdf, y0pdf, x1pdf, y1pdf] = apiBBox;
            const x0 = Math.round(x0pdf * scale * dpr);
            const y0 = Math.round(y0pdf * scale * dpr);
            const width = Math.max(0, Math.round((x1pdf - x0pdf) * scale * dpr));
            const height = Math.max(0, Math.round((y1pdf - y0pdf) * scale * dpr));

            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = width;
            tempCanvas.height = height;
            const tempCtx = tempCanvas.getContext('2d');
            tempCtx.drawImage(canvas, x0, y0, width, height, 0, 0, width, height);
            const base64Image = tempCanvas.toDataURL('image/png');

            if (width <= 0 || height <= 0) {
                console.error('❌ Invalid canvas dimensions:', {width, height});
            }

            onFinishDrawing();

            // --- Call backend with apiFetch (auto token attach + refresh) ---
            try {
                const res = await apiFetch(`${API_BASE}/analyze-snippet`, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        image: base64Image,
                        page: pageNum,
                        bbox: apiBBox,
                        provider,
                        model,
                    }),
                });

                let data = {};
                try {
                    data = await res.json();
                } catch {
                    // non-JSON error body, keep data = {}
                }

                if (!res.ok) {
                    const msg = data?.error || `HTTP ${res.status}`;
                    throw new Error(msg);
                }

                // Normalize backend response into a full-fledged annotation object
                const ttype = (data?.type || '').toLowerCase();
                let replacement = null;

                if (ttype === 'key_value') {
                    replacement = {
                        type: 'key_value',
                        page: pageNum,
                        // keep only value bbox for stable keys in your UI
                        value: {text: String(data.value || ''), bbox: uiBBox},
                        field: {text: String(data.field || '')},
                    };
                } else if (ttype === 'entity') {
                    replacement = {
                        type: 'entity',
                        page: pageNum,
                        bbox: uiBBox,
                        text: String(data.text || ''),
                        entity_type: String(data.entity_type || 'unknown'),
                    };
                } else if (ttype === 'summary_row') {
                    replacement = {
                        type: 'summary_row',
                        page: pageNum,
                        bbox: uiBBox,
                        label: String(data.label || 'Summary'),
                        text: String(data.text || ''),
                    };
                } else if (ttype === 'table') {
                    replacement = {
                        type: 'table',
                        page: pageNum,
                        bbox: uiBBox,
                        label: data.label || 'Table',
                        table_header: Array.isArray(data.table_header) ? data.table_header : [],
                        rows: Array.isArray(data.rows) ? data.rows : [],
                    };
                } else if (data?.key || data?.value) {
                    // Legacy fallback: treat as KV if the model didn’t set type
                    replacement = {
                        type: 'key_value',
                        page: pageNum,
                        value: {text: String(data.value || ''), bbox: uiBBox},
                        field: {text: String(data.key || '')},
                    };
                }

                if (!replacement) {
                    throw new Error('Unrecognized snippet result');
                }

                updateAnnotation?.(stableKey, {__replaceWith: replacement, status: 'done'});
            } catch (err) {
                console.error('Error sending image snippet:', err);
                updateAnnotation?.(stableKey, {
                    __replaceWith: {
                        type: 'entity',
                        page: pageNum,
                        bbox: uiBBox,
                        text: t('error'),
                        entity_type: 'error',
                    },
                    status: 'error',
                });
            } finally {
                setDrawing(false);
                setStartPoint(null);
                setCurrentRect(null);
                setCurrentPage(null);
            }
        } else {
            setDrawing(false);
            setStartPoint(null);
            setCurrentRect(null);
            setCurrentPage(null);
        }
    };


    return {
        containerRef,
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        currentRect,
        currentPage,
    };
}
